import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  Icon,
  PaginationComponent,
  RSelect,
} from "../../../../components/Component";
import { left_select_options } from "./table_list_data/table_list_heading";
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import { useLocation } from "react-router-dom";
import { user_list_column } from "./table_list_data/table_list_columns";
import {
  BranchLocationRows,
  ClientDeliveryNoteListRows,
  ClientGoodsReceivedListRows,
  CompanyUserAccountListRows,
  CustomerUserAccountListRows,
  DeliveryNoteTicketListRows,
  EmpDeptRolesListRows,
  EmployeeListRows,
  GoodsReceivedWeighBridgeTicketListRows,
  GrnSchedulerListRows,
  LoadingOrderListRows,
  ProductRows,
  ProductTypeRows,
  SmartInvoiceListRows,
  TruckDriverUserAccountListRows,
  UnitMeasurementRows,
  UserListRows,
  WarehouseInventoryListRows,
} from "./table_list_data/table_list_rows";
import * as app_consts from "../../../../constants/react_app_consts";
import usePersistentState from "../../../../Hooks/usePersistentState";
import { filterRowsByTableSearch } from "../../../../utils/tableSearch";
import { usePageSearch } from "../../../../layout/header-search/PageSearchContext";

const noop = () => {};

const TableList = ({
  left_head_select,
  type,
  data = [],
  rowOptionsData,
  amendFunc,
  editFunc,
  setOpendModalDetails,
  setSelectedData,
  placeholder,
  search_placeholder,
  searchFunction = noop,
}) => {
  const location = useLocation();
  const [left_select_data, setLeftSelectData] = useState([]);
  const [columnData, setColumnData] = useState([]);
  const [tableData, setTableData] = useState(data);
  const tableMemoryKey = useMemo(
    () => `table_list_memory:${location.pathname}:${type}`,
    [location.pathname, type]
  );
  const [currentPage, setCurrentPage] = usePersistentState(`${tableMemoryKey}:currentPage`, 1);
  const [itemPerPage, setItemPerPage] = usePersistentState(`${tableMemoryKey}:itemPerPage`, 10);
  const [sort, setSortState] = usePersistentState(`${tableMemoryKey}:sort`, "dsc");

  const [rowOptData, setRowOptData] = useState(rowOptionsData);
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const { query: globalSearchQuery, setQuery: setGlobalSearchQuery } = usePageSearch();

  useEffect(() => {
    setLeftSelectData(left_select_options(type));
    setColumnData(user_list_column(type));
  }, [type]);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  useEffect(() => {
    setRowOptData(rowOptionsData);
  }, [rowOptionsData]);

  useEffect(() => {
    setLocalSearchQuery(globalSearchQuery);
  }, [globalSearchQuery]);

  const effectiveSearchQuery = globalSearchQuery || localSearchQuery;

  useEffect(() => {
    setCurrentPage(1);
  }, [effectiveSearchQuery, setCurrentPage]);

  useEffect(() => {
    searchFunction(effectiveSearchQuery);
  }, [effectiveSearchQuery, searchFunction]);

  const searchableHeaders = useMemo(
    () => columnData.map((column) => column?.label).filter(Boolean),
    [columnData],
  );

  const filteredTableData = useMemo(
    () =>
      filterRowsByTableSearch({
        rows: tableData,
        query: effectiveSearchQuery,
        headers: searchableHeaders,
      }),
    [effectiveSearchQuery, searchableHeaders, tableData],
  );

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = filteredTableData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredTableData.length / itemPerPage));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [currentPage, filteredTableData.length, itemPerPage, setCurrentPage]);

  const sortFunc = () => {
    if (type === app_consts.GOODS_RECEIVED_WEIGH_BRIDGE_TICKET_LIST) {
      let sortedData;

      if (sort === "dsc") {
        sortedData = [...tableData].sort((a, b) =>
          b.goods_received.goods_received_ref_number.localeCompare(a.goods_received.goods_received_ref_number),
        );
      } else if (sort === "asc") {
        sortedData = [...tableData].sort((a, b) =>
          a.goods_received.goods_received_ref_number.localeCompare(b.goods_received.goods_received_ref_number),
        );
      }

      if (sortedData) setTableData(sortedData);
    }
  };

  const isWarehouseInventoryTable = type === app_consts.WAREHOUSE_INVENTORY_LIST;

  return (
    <>
      <style>
        {`
    .warehouse-inventory-table,
    .warehouse-inventory-table.card-stretch,
    .warehouse-inventory-table .card-inner,
    .warehouse-inventory-table .table-responsive,
    .warehouse-inventory-table .dataTables_wrapper,
    .warehouse-inventory-table .nk-tb-list {
      background: transparent !important;
      box-shadow: none !important;
      border: none !important;
    }

    .warehouse-inventory-table .card-inner {
      border: none !important;
    }

    .warehouse-inventory-table .nk-tb-head {
      border-bottom: none !important;
      background: transparent !important;
    }

    .warehouse-inventory-table .nk-tb-item {
      margin-bottom: 10px !important;
      background: #fff !important;
      border-radius: 10px !important;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06) !important;
      overflow: hidden !important;
    }

    .warehouse-inventory-table .nk-tb-item .nk-tb-col {
      padding-top: 14px !important;
      padding-bottom: 14px !important;
      background: transparent !important;
      border-top: none !important;
    }
  `}
      </style>

      <DataTable
        className={`card-stretch ${
          isWarehouseInventoryTable ? "warehouse-inventory-table nk-tb-list is-separate is-medium" : ""
        }`}
      >
        <div
          className="position-relative card-tools-toggle"
          style={
            isWarehouseInventoryTable
              ? {
                  background: "transparent",
                  boxShadow: "none",
                  border: "none",
                  padding: "1rem 1.25rem",
                }
              : {}
          }
        >
          <div className="card-title-group">
            <div className="card-tools">
              <div className="form-inline flex-nowrap gx-3">
                <div className="form-wrap">
                  {left_head_select && (
                    <RSelect options={left_select_data} className="w-130px" placeholder={placeholder} />
                  )}
                </div>
              </div>
            </div>

            <div className="card-tools flex-grow-1 px-2">
              <div className="form-control-wrap">
                <div className="form-icon form-icon-left">
                  <Icon name="search" />
                </div>
                <input
                  type="text"
                  className="form-control"
                  value={effectiveSearchQuery}
                  onChange={(event) => {
                    setLocalSearchQuery(event.target.value);
                    setGlobalSearchQuery(event.target.value);
                  }}
                  placeholder={search_placeholder || "Search table headers and details"}
                />
              </div>
            </div>

            <div className="card-tools me-n1">
              <ul className="btn-toolbar gx-1">
                <li className="btn-toolbar-sep"></li>

                <li>
                  <UncontrolledDropdown>
                    <DropdownToggle color="tranparent" className="btn btn-trigger btn-icon dropdown-toggle">
                      <Icon name="setting"></Icon>
                    </DropdownToggle>

                    <DropdownMenu end className="dropdown-menu-xs">
                      <ul className="link-check">
                        <li>
                          <span>Show</span>
                        </li>
                        <li className={itemPerPage === 10 ? "active" : ""}>
                          <DropdownItem
                            tag="a"
                            href="#dropdownitem"
                            onClick={(ev) => {
                              ev.preventDefault();
                              setItemPerPage(10);
                              setCurrentPage(1);
                            }}
                          >
                            10
                          </DropdownItem>
                        </li>
                        <li className={itemPerPage === 15 ? "active" : ""}>
                          <DropdownItem
                            tag="a"
                            href="#dropdownitem"
                            onClick={(ev) => {
                              ev.preventDefault();
                              setItemPerPage(15);
                              setCurrentPage(1);
                            }}
                          >
                            15
                          </DropdownItem>
                        </li>
                      </ul>

                      <ul className="link-check">
                        <li>
                          <span>Order</span>
                        </li>
                        <li className={sort === "dsc" ? "active" : ""}>
                          <DropdownItem
                            tag="a"
                            href="#dropdownitem"
                            onClick={(ev) => {
                              ev.preventDefault();
                              setSortState("dsc");
                              sortFunc();
                            }}
                          >
                            DESC
                          </DropdownItem>
                        </li>
                        <li className={sort === "asc" ? "active" : ""}>
                          <DropdownItem
                            tag="a"
                            href="#dropdownitem"
                            onClick={(ev) => {
                              ev.preventDefault();
                              setSortState("asc");
                              sortFunc();
                            }}
                          >
                            ASC
                          </DropdownItem>
                        </li>
                      </ul>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <DataTableBody>
          <DataTableHead>
            {columnData &&
              columnData?.map((column) => (
                <DataTableRow key={column.key}>
                  <span>{column.label}</span>
                </DataTableRow>
              ))}
            <DataTableRow className="nk-tb-col-tools">&nbsp;</DataTableRow>
          </DataTableHead>

          {type === app_consts.USER_LIST_TYPE ? (
            <UserListRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              editFunction={editFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
            />
          ) : type === app_consts.BRANCH_LOCATION_LIST ? (
            <BranchLocationRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.PRODUCT_LIST ? (
            <ProductRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.PRODUCT_TYPE_LIST ? (
            <ProductTypeRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.UNIT_MEASUREMENT_LIST ? (
            <UnitMeasurementRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.EMPLOYEE_LIST_TYPE ? (
            <EmployeeListRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.GOODS_RECEIVED_WEIGH_BRIDGE_TICKET_LIST ? (
            <GoodsReceivedWeighBridgeTicketListRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.WAREHOUSE_INVENTORY_LIST ? (
            <WarehouseInventoryListRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.DELIVERY_NOTES_WEIGH_BRIDGE_TICKET_LIST ? (
            <DeliveryNoteTicketListRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.CLIENT_GOODS_RECEIVED_LIST ? (
            <ClientGoodsReceivedListRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.CUSTOMER_USER_ACCOUNT_LIST ? (
            <CustomerUserAccountListRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.CLIENT_DELIVERY_NOTES_LIST ? (
            <ClientDeliveryNoteListRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.COMPANY_USER_ACCOUNT_LIST ? (
            <CompanyUserAccountListRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.TRUCK_DRIVER_USER_ACCOUNT_LIST ? (
            <TruckDriverUserAccountListRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.DEPARTMENT_AND_ROLES_LIST ? (
            <EmpDeptRolesListRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.SMART_INVOICE_LIST ? (
            <SmartInvoiceListRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.LOADING_ORDERS_LIST ? (
            <LoadingOrderListRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : type === app_consts.GRN_SCHEDULER_LIST ? (
            <GrnSchedulerListRows
              data={tableData}
              currentItems={currentItems}
              rowOptionsData={rowOptData}
              amendFunction={amendFunc}
              setOpendModalDetails={setOpendModalDetails}
              setSelectedData={setSelectedData}
              type={type}
            />
          ) : (
            ""
          )}
        </DataTableBody>

        <div
          className={isWarehouseInventoryTable ? "" : "card-inner"}
          style={
            isWarehouseInventoryTable
              ? {
                  background: "transparent",
                  boxShadow: "none",
                  border: "none",
                  padding: "1rem 1.25rem",
                }
              : {}
          }
        >
          {currentItems.length > 0 ? (
            <PaginationComponent
              itemPerPage={itemPerPage}
              totalItems={filteredTableData.length}
              paginate={paginate}
              currentPage={currentPage}
            />
          ) : (
            <div className="text-center">
              <span className="text-silent">No data found</span>
            </div>
          )}
        </div>
      </DataTable>
    </>
  );
};

export default TableList;
